import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './MediaCard.module.css';
import InfoTag from '../InfoTag/InfoTag';
import AccessBadge from '../AccessBadge/AccessBadge';
import StatusBadge from '../StatusBadge/StatusBadge';

type MediaCardBadge = {
  label: string;
  variant: 'free' | 'premium' | 'in-progress' | 'none';
  size?: 'default' | 'sm';
};

interface MediaCardProps {
  image?: string;
  imageAlt?: string;
  imageSrcSet?: string;
  flairColor?: string;
  imageContain?: boolean;
  badge?: MediaCardBadge;
  title: string;
  subtitle?: string;
  description?: string;
  meta?: string;
  tags?: string[];
  isActive?: boolean;
  href?: string;
  className?: string;
  onQuickStart?: () => void;
  quickStartLoading?: boolean;
}


function renderBadge(badge: MediaCardBadge) {
  if (badge.variant === 'free') return <AccessBadge size={badge.size}>{badge.label}</AccessBadge>;
  if (badge.variant === 'premium') return <AccessBadge variant="premium" size={badge.size}>{badge.label}</AccessBadge>;
  if (badge.variant === 'in-progress') return <StatusBadge variant="in-progress">{badge.label}</StatusBadge>;
  return null;
}

export default function MediaCard({
  image,
  imageAlt = '',
  imageSrcSet,
  flairColor,
  imageContain = false,
  badge,
  title,
  subtitle,
  description,
  meta,
  tags,
  isActive,
  href,
  className,
  onQuickStart,
  quickStartLoading = false,
}: MediaCardProps) {
  const { t } = useTranslation('common');
  const showBadge = badge != null && badge.variant !== 'none';

  const content = (
    <>
      {image && (
        <div
          className={[styles.imageWrapper, imageContain ? styles.imageContain : ''].filter(Boolean).join(' ')}
          style={flairColor ? { '--img-bg': flairColor } as CSSProperties : undefined}
        >
          <img src={image} srcSet={imageSrcSet} alt={imageAlt} className={styles.image} loading="lazy" />
          {showBadge && (
            <span className={styles.badgeOverlay}>
              {renderBadge(badge)}
            </span>
          )}
          {onQuickStart && (
            <button
              className={styles.quickStartBtn}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onQuickStart();
              }}
              aria-label={quickStartLoading ? t('loading') : t('quickStart')}
              disabled={quickStartLoading}
            >
              {quickStartLoading ? (
                <svg className={styles.quickStartSpinner} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" strokeDasharray="50" strokeDashoffset="15" strokeLinecap="round" />
                </svg>
              ) : (
                <>{t('quickStart')} &rarr;</>
              )}
            </button>
          )}
        </div>
      )}
      <div className={styles.body}>
        {!image && showBadge && renderBadge(badge)}
        <h3 className={styles.title}>{title}</h3>
        {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        {meta && <span className={styles.meta}>{meta}</span>}
        {description && <span className={styles.description}>{description}</span>}
        {tags && tags.length > 0 && (
          <div className={styles.tags}>
            {tags.map((tag) => (
              <InfoTag key={tag}>{tag}</InfoTag>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const cardClass = [styles.card, isActive ? styles.active : '', className ?? ''].filter(Boolean).join(' ');

  if (href) {
    return <Link to={href} className={cardClass}>{content}</Link>;
  }

  return <div className={cardClass}>{content}</div>;
}
